/**
 * Seite "Einstellungen des Roboters" im Reiter API.
 *
 * Der Inhalt steht in roboter-einstellungen.ts und ist dort an die Firmware
 * angebunden; hier wird er nur dargestellt.
 */

import { Link, Text } from "@microbit/ui";
import { ReactNode, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, HStack, VStack } from "styled-system/jsx";
import CodeEmbed from "../common/CodeEmbed";
import { Einstellung, FesterWert, Gruppe, Zeitpunkt } from "./roboter-einstellungen";
import { einstellungsseite } from "./roboter-einstellungen-sprachen";

/** Kennung der Seite im Reiter API, als Anker in der Adresse. */
export const EINSTELLUNGEN_ID = "roboter-einstellungen";

/**
 * Aufrufe und Python-Namen im Fliesstext in Codeschrift.
 *
 * Im Datenbestand steht reiner Text, damit der Prueflauf die Aufrufe darin
 * finden kann. "drive.velocity()" in normaler Schrift geht im Satz aber unter.
 */
export const CODE_IM_TEXT =
  /(\b[a-z_]+\.[a-z_]+\([^()]*\)|\b(?:RobotBusyError|RobotFaultError|ValueError|PinError|None|True|False)\b|"[a-z]+")/g;

export const MitCode = ({ text }: { text: string }) => {
  // Schluessel aus Stellung im Text und Inhalt: die Teile eines festen Textes
  // aendern sich nie, und derselbe Aufruf kann zweimal vorkommen.
  let stelle = 0;
  const teile = text.split(CODE_IM_TEXT).map((teil, index) => {
    const schluessel = `${stelle}:${teil}`;
    stelle += teil.length;
    return { teil, schluessel, istCode: index % 2 === 1 };
  });
  return (
    <>
      {teile.map(({ teil, schluessel, istCode }) =>
        istCode ? (
          <Text as="code" key={schluessel} fontFamily="code" fontSize="0.92em">
            {teil}
          </Text>
        ) : (
          <span key={schluessel}>{teil}</span>
        )
      )}
    </>
  );
};

const Merkmal = ({ label, children }: { label: ReactNode; children: string }) => (
  <HStack alignItems="baseline" gap="2">
    <Text fontSize="xs" color="fg.muted" minWidth="5.5rem" flexShrink={0}>
      {label}
    </Text>
    <Text fontSize="sm">{children}</Text>
  </HStack>
);

const EinstellungEintrag = ({
  einstellung,
  zeitpunkt,
}: {
  einstellung: Einstellung;
  zeitpunkt: Record<Zeitpunkt, string>;
}) => (
  <VStack
    alignItems="stretch"
    gap="2"
    py="3"
    borderBottomWidth="1px"
    borderColor="gray.100"
    data-testid="einstellung"
  >
    <Text as="h4" fontSize="md" fontWeight="semibold">
      {einstellung.name}
    </Text>
    <CodeEmbed code={einstellung.aufruf} toolkitType="api" />
    <VStack alignItems="stretch" gap="1">
      <Merkmal label={<FormattedMessage id="robot-settings-default" />}>
        {einstellung.vorgabe}
      </Merkmal>
      {einstellung.bereich && (
        <Merkmal label={<FormattedMessage id="robot-settings-allowed" />}>
          {einstellung.bereich}
        </Merkmal>
      )}
      <Merkmal label={<FormattedMessage id="robot-settings-when" />}>
        {zeitpunkt[einstellung.wann]}
      </Merkmal>
    </VStack>
    <Text fontSize="sm">
      <MitCode text={einstellung.wirkung} />
    </Text>
    {einstellung.hinweis && (
      <Box
        bg="brand.50"
        borderLeftWidth="3px"
        borderColor="brand.500"
        px="3"
        py="2"
        borderRadius="sm"
      >
        <Text fontSize="sm">
          <MitCode text={einstellung.hinweis} />
        </Text>
      </Box>
    )}
  </VStack>
);

const FesteWerte = ({ werte }: { werte: FesterWert[] }) => (
  <VStack alignItems="stretch" gap="2" pt="3">
    <Text fontSize="xs" fontWeight="semibold" color="fg.muted" textTransform="uppercase">
      <FormattedMessage id="robot-settings-fixed" />
    </Text>
    {werte.map((wert) => (
      <Box key={wert.name}>
        <HStack alignItems="baseline" gap="2" flexWrap="wrap">
          <Text fontSize="sm" fontWeight="semibold">
            {wert.name}
          </Text>
          <Text fontSize="sm" fontFamily="code">
            {wert.wert}
          </Text>
        </HStack>
        <Text fontSize="sm" color="fg.muted">
          <MitCode text={wert.bedeutung} />
        </Text>
      </Box>
    ))}
  </VStack>
);

const Abschnitt = ({
  gruppe,
  zeitpunkt,
}: {
  gruppe: Gruppe;
  zeitpunkt: Record<Zeitpunkt, string>;
}) => (
  <VStack
    as="section"
    id={`einstellungen-${gruppe.id}`}
    alignItems="stretch"
    gap="1"
    pt="5"
    data-testid="einstellungen-abschnitt"
  >
    <Text as="h3" fontSize="lg" fontWeight="semibold">
      {gruppe.titel}
    </Text>
    <Text fontSize="sm">
      <MitCode text={gruppe.einleitung} />
    </Text>
    {gruppe.ablesen && (
      <HStack gap="2" alignItems="baseline" pt="1">
        <Text fontSize="xs" color="fg.muted">
          <FormattedMessage id="robot-settings-read-current" />
        </Text>
        <Text fontSize="sm" fontFamily="code">
          {gruppe.ablesen}
        </Text>
      </HStack>
    )}
    {gruppe.einstellungen.map((einstellung) => (
      <EinstellungEintrag
        key={einstellung.name}
        einstellung={einstellung}
        zeitpunkt={zeitpunkt}
      />
    ))}
    {gruppe.fest && gruppe.fest.length > 0 && <FesteWerte werte={gruppe.fest} />}
  </VStack>
);

const RoboterEinstellungen = () => {
  const intl = useIntl();
  const seite = einstellungsseite(intl.locale);
  const springe = useCallback((id: string) => {
    document
      .getElementById(`einstellungen-${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);
  return (
    <VStack alignItems="stretch" gap="2" px="3" pb="8">
      <VStack as="section" alignItems="stretch" gap="2" pt="3">
        <Text as="h3" fontSize="lg" fontWeight="semibold">
          <FormattedMessage id="robot-settings-principles" />
        </Text>
        <VStack as="ul" alignItems="stretch" gap="2" pl="5" listStyleType="disc">
          {seite.grundsaetze.map((satz) => (
            <Text as="li" key={satz} fontSize="sm">
              <MitCode text={satz} />
            </Text>
          ))}
        </VStack>
      </VStack>
      <VStack
        as="nav"
        alignItems="flex-start"
        gap="1"
        pt="4"
        aria-label={intl.formatMessage({ id: "robot-settings-contents" })}
      >
        <Text fontSize="xs" fontWeight="semibold" color="fg.muted" textTransform="uppercase">
          <FormattedMessage id="robot-settings-contents" />
        </Text>
        {seite.gruppen.map((gruppe) => (
          <Link
            key={gruppe.id}
            href={`#einstellungen-${gruppe.id}`}
            color="brand.500"
            fontSize="sm"
            onClick={(event) => {
              event.preventDefault();
              springe(gruppe.id);
            }}
          >
            {gruppe.titel}
          </Link>
        ))}
      </VStack>
      {seite.gruppen.map((gruppe) => (
        <Abschnitt key={gruppe.id} gruppe={gruppe} zeitpunkt={seite.zeitpunkt} />
      ))}
    </VStack>
  );
};

export default RoboterEinstellungen;
